import type { PatchOp, StagedEntity } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import type { DdlOp } from "../shared/mcp-chat-types";

/**
 * Human-readable label for each KBranching `EJsonEntityType`. Drives the
 * summary rendered in the rewind confirmation dialog.
 *
 * Backend serializes the enum as an int, not a string, so we also map the
 * numeric values (0-indexed, matching EJsonEntityType declaration order in
 * Backend/CoreApi.Data/Entities/BranchingService/KBranchItem.cs). Without
 * the numeric keys, the UI falls back to displaying the raw enum ordinal
 * ("1 3 will be reverted" instead of "1 screen will be reverted").
 */
const ENTITY_TYPE_LABELS: Record<string, string> = {
  KAction: "action",
  KDatasource: "datasource",
  KProject: "project",
  KScreenDesign: "screen",
  KWorkFlow: "workflow",
  KApiManagement: "API",
  KNotification: "notification",
  KScheduledJobs: "scheduled job",
  KOfflineTable: "offline table",
  "0": "action",
  "1": "datasource",
  "2": "project",
  "3": "screen",
  "4": "workflow",
  "5": "API",
  "6": "notification",
  "7": "scheduled job",
  "8": "offline table"
};

/** Virtual bucket type for component-level changes inside a screen entity. */
const COMPONENT_BUCKET_KEY = "__component";

function pluralize(count: number, label: string): string {
  if (count === 1) return `1 ${label}`;
  const plural = label.endsWith("y") ? label.slice(0, -1) + "ies" : label + "s";
  return `${count} ${plural}`;
}

/** Shorten a uuid to "ab12cd" for fallback labels. */
function shortId(id: string): string {
  const stripped = id.replace(/-/g, "");
  return stripped.slice(0, 6);
}

/**
 * Extract the deepest component kuikaId from a staged-op path. Returns null
 * when the op is not component-level (root-path, language metadata, project
 * settings, etc.). The `[id=-1]` root container is excluded — only real
 * component ids are returned.
 */
function extractComponentKuikaId(path: string | undefined): number | null {
  if (!path || !path.startsWith("/JSON/displayItems/")) return null;
  const matches = [...path.matchAll(/\[id=(-?\d+)\]/g)];
  for (let i = matches.length - 1; i >= 0; i--) {
    const id = Number.parseInt(matches[i][1], 10);
    if (!Number.isNaN(id) && id !== -1) return id;
  }
  return null;
}

/**
 * Detect whether a staged op represents an outright component add/remove
 * (where the path's final segment is the component's own `[id=N]` slot),
 * as opposed to a property-level change inside an existing component.
 * Used to compose count headlines ("N components will be deleted / added").
 */
function isComponentRootOp(path: string | undefined): boolean {
  if (!path || !path.startsWith("/JSON/displayItems/")) return false;
  const match = path.match(/\[id=(-?\d+)\]$/);
  if (!match) return false;
  const id = Number.parseInt(match[1], 10);
  return !Number.isNaN(id) && id !== -1;
}

export interface RewindEntityItem {
  entityId: string;
  label: string;
}

export interface RewindEntityBucket {
  /** kuika entity-type enum name (e.g. "KScreenDesign") OR the virtual
   *  component key. Drives label selection in `formatSummaryLines`. */
  entityType: string;
  created: RewindEntityItem[];
  modified: RewindEntityItem[];
}

export interface RewindSummary {
  /** opIds that will be discarded via DiscardDraftStagedOps. */
  opIdsToDiscard: string[];
  /** Per-entity-type rollup — drives the human-readable summary lines. */
  buckets: RewindEntityBucket[];
  /** DDL operations collected from tool activity; won't be reverted. */
  ddlOps: DdlOp[];
  /** Count of messages that will be removed from the conversation. */
  messagesToDeleteCount: number;
  /** True when the diff yielded no discardable ops (nothing to rewind). */
  isEmpty: boolean;
}

interface PerTypeBucketInternal {
  /** entityId → label */
  created: Map<string, string>;
  modified: Map<string, string>;
}

/**
 * Callback the caller provides to resolve a component kuikaId to a
 * human-friendly label (e.g. via the designer's screenDesignDictionary).
 * Returning null/undefined falls back to "Component #N".
 */
export type ComponentLabelResolver = (kuikaId: number) => string | null | undefined;

interface ScreenChangeClassification {
  /** Component-level patches — grouped by componentId so the same
   *  component isn't double-counted when multiple props change. */
  byComponent: Map<number, { added: boolean; removed: boolean; touched: boolean }>;
  /** True when at least one patch targets screen metadata (e.g. language,
   *  top-level property) rather than a component. */
  hasScreenMetadataChange: boolean;
}

function classifyScreenPatches(newOps: PatchOp[]): ScreenChangeClassification {
  const byComponent = new Map<number, { added: boolean; removed: boolean; touched: boolean }>();
  let hasScreenMetadataChange = false;

  for (const op of newOps) {
    const componentId = extractComponentKuikaId(op.path);
    if (componentId === null) {
      hasScreenMetadataChange = true;
      continue;
    }
    const bucket = byComponent.get(componentId) ?? { added: false, removed: false, touched: false };
    if (isComponentRootOp(op.path)) {
      if (op.op === "add") bucket.added = true;
      else if (op.op === "remove") bucket.removed = true;
      else bucket.touched = true;
    } else {
      bucket.touched = true;
    }
    byComponent.set(componentId, bucket);
  }

  return { byComponent, hasScreenMetadataChange };
}

/**
 * Compute the rewind summary for a specific user message.
 *
 * Strategy:
 *   1. Every op present in `currentEntities` but NOT in the baseline
 *      (captured when the message was sent) is marked for discard.
 *      Rewind is intentionally project-wide, not tab-scoped — the history
 *      modal presents a unified timeline across all tabs, and reverting to
 *      any checkpoint rolls the project back to that timestamp regardless
 *      of which chat produced each op.
 *   2. For KScreenDesign modifications, patch paths are introspected so a
 *      "button was added to the home screen" shows up as "1 component added"
 *      with the component's friendly name — not as "1 screen reverted".
 *      Truly screen-level edits (metadata, language, root properties) still
 *      land in the screen bucket.
 *   3. Other entity types (action, datasource, etc.) remain grouped by type.
 */
export function computeRewindSummary(
  baselineOpIds: string[] | undefined,
  currentEntities: StagedEntity[],
  ddlOps: DdlOp[],
  messagesToDeleteCount: number,
  resolveComponentLabel?: ComponentLabelResolver
): RewindSummary {
  const baseline = new Set(baselineOpIds ?? []);
  const opIdsToDiscard: string[] = [];
  const perType = new Map<string, PerTypeBucketInternal>();

  const bucketFor = (key: string): PerTypeBucketInternal => {
    const existing = perType.get(key);
    if (existing) return existing;
    const fresh: PerTypeBucketInternal = { created: new Map<string, string>(), modified: new Map<string, string>() };
    perType.set(key, fresh);
    return fresh;
  };

  for (const entity of currentEntities) {
    if (!entity.patch || !entity.entityType || !entity.entityId) continue;
    const newOps = entity.patch.filter((op) => op.opId && !baseline.has(op.opId));
    if (newOps.length === 0) continue;

    for (const op of newOps) {
      if (op.opId) opIdsToDiscard.push(op.opId);
    }

    const entityTypeKey = String(entity.entityType);
    const isScreen = entityTypeKey === "KScreenDesign" || entityTypeKey === "3";
    // Root-path "add" means "new entity snapshot inserted" — a row creation in
    // KScreenDesign / KAction / KDatasource / etc. BranchingSaveInterceptor
    // always sets Path="/" for root snapshots; any other value means a
    // nested property mutation.
    const isRowCreation = newOps.some((op) => op.op === "add" && op.path === "/");

    if (isScreen && !isRowCreation) {
      // Split the screen's patches into component-level and screen-level edits
      // so the summary matches user intent: adding a button reads as
      // "1 component" not "1 screen".
      const classification = classifyScreenPatches(newOps);
      if (classification.byComponent.size > 0) {
        const componentBucket = bucketFor(COMPONENT_BUCKET_KEY);
        for (const [componentId, flags] of classification.byComponent.entries()) {
          const key = String(componentId);
          const label = resolveComponentLabel?.(componentId) || `Component #${componentId}`;
          if (flags.added) componentBucket.created.set(key, label);
          else if (flags.removed) componentBucket.modified.set(key, label); // revert of a removal = resurrection, still an in-place change
          else componentBucket.modified.set(key, label);
        }
      }
      if (classification.hasScreenMetadataChange) {
        const screenBucket = bucketFor(entityTypeKey);
        const screenLabel = entity.entityLabel?.trim() || `Screen ${shortId(entity.entityId)}`;
        screenBucket.modified.set(entity.entityId, screenLabel);
      }
      continue;
    }

    const bucket = bucketFor(entityTypeKey);
    const typeLabel = ENTITY_TYPE_LABELS[entityTypeKey] || entityTypeKey;
    const label = entity.entityLabel?.trim() || `${typeLabel} ${shortId(entity.entityId)}`;
    if (isRowCreation) bucket.created.set(entity.entityId, label);
    else bucket.modified.set(entity.entityId, label);
  }

  const isProject = (t: string): boolean => t === "KProject" || t === "2";
  const hasSignificantNonProjectBucket = Array.from(perType.keys()).some(
    (t) => !isProject(t) && (perType.get(t)!.created.size > 0 || perType.get(t)!.modified.size > 0)
  );

  const buckets: RewindEntityBucket[] = [];
  for (const [entityType, maps] of perType.entries()) {
    // Suppress KProject whenever another entity also changed in this scope.
    // Rationale: writes to a screen/action/datasource ripple into the project
    // snapshot, so naive counting says "1 screen will be deleted, 1 project
    // will be reverted" — user reads 2 when only 1 thing was intended.
    // Tradeoff: if the user both renamed the project and edited a screen in
    // the same turn, the project rename disappears from the summary. The
    // rewind still rolls it back correctly; only the display omits it. Kept
    // simple because the ripple case is overwhelmingly the common one.
    if (isProject(entityType) && hasSignificantNonProjectBucket) continue;
    buckets.push({
      entityType,
      created: Array.from(maps.created, ([entityId, label]) => ({ entityId, label })),
      modified: Array.from(maps.modified, ([entityId, label]) => ({ entityId, label }))
    });
  }

  return {
    opIdsToDiscard,
    buckets,
    ddlOps,
    messagesToDeleteCount,
    isEmpty: opIdsToDiscard.length === 0 && ddlOps.length === 0
  };
}

export interface RewindSummaryLine {
  kind: "revert" | "delete" | "conversation";
  /** Group heading, e.g. "2 screens will be deleted". */
  headline: string;
  /** Names of the affected entities (empty for conversation line). */
  names: string[];
}

function typeLabelFor(bucketType: string): string {
  if (bucketType === COMPONENT_BUCKET_KEY) return "component";
  return ENTITY_TYPE_LABELS[bucketType] || bucketType;
}

/**
 * Render the rewind buckets into flat lines for display. Each bucket
 * becomes up to two lines (created + modified) that carry both a count
 * headline and the specific entity names so the dialog can tell the user
 * exactly *which* entities are affected — counts alone are ambiguous.
 */
export function formatSummaryLines(summary: RewindSummary): RewindSummaryLine[] {
  const lines: RewindSummaryLine[] = [];
  for (const bucket of summary.buckets) {
    const typeLabel = typeLabelFor(bucket.entityType);
    if (bucket.created.length > 0) {
      const verb = bucket.entityType === COMPONENT_BUCKET_KEY ? "will be removed" : "will be deleted";
      lines.push({
        kind: "delete",
        headline: pluralize(bucket.created.length, typeLabel) + " " + verb,
        names: bucket.created.map((e) => e.label)
      });
    }
    if (bucket.modified.length > 0) {
      lines.push({
        kind: "revert",
        headline: pluralize(bucket.modified.length, typeLabel) + " will be reverted",
        names: bucket.modified.map((e) => e.label)
      });
    }
  }
  if (summary.messagesToDeleteCount > 0) {
    lines.push({
      kind: "conversation",
      headline: pluralize(summary.messagesToDeleteCount, "message") + " will be removed from conversation",
      names: []
    });
  }
  return lines;
}

/** Map DDL op to a short human description. */
export function formatDdlOp(op: DdlOp): string {
  const verb = op.operation ?? "change";
  const target = op.target ? ` ${op.target}` : "";
  return `${verb}${target}`;
}

/**
 * Format a past timestamp relative to now: "just now", "5m ago", "2h ago",
 * "3d ago", or an absolute date for anything older. No external dep.
 */
export function formatRelativeTime(timestamp: number, now: number = Date.now()): string {
  const diffMs = Math.max(0, now - timestamp);
  const sec = Math.floor(diffMs / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(timestamp).toLocaleDateString("en-US");
}

/** Truncate a string to `max` characters with an ellipsis suffix. */
export function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max - 1).trimEnd() + "…";
}

export interface CheckpointDescriptor {
  /** The user-message id — passed to `handleRewindRequest` when user picks. */
  userMsgId: string;
  /** The chat tab this checkpoint belongs to. Needed so the history modal
   *  can switch to the right tab before dispatching the rewind. */
  tabId: string;
  /** Display name of the owning chat tab (e.g. "Chat 1" / custom name). */
  tabName: string;
  /** When the message was sent (ms epoch). */
  timestamp: number;
  /** Trimmed message content, ready for display. */
  preview: string;
  /** Number of subsequent conversation messages that would be removed. */
  messagesAffected: number;
  /** Fully computed revert summary against the current backend state. */
  summary: RewindSummary;
  /** True when the owning chat was closed/cleared (tab no longer in UI
   *  but retained on the server for 30 days). Drives the "Closed" badge
   *  and skips the tab-switch in the revert path. */
  isArchived: boolean;
}

export interface CheckpointMessage {
  id: string;
  role: string;
  content: string;
  timestamp: number;
  stagedOpIdsBeforeSend?: string[];
  ddlOps?: DdlOp[];
}

export interface CheckpointTabInput {
  tabId: string;
  tabName: string;
  messages: CheckpointMessage[];
  /** True when this tab comes from the archived-sessions feed rather than
   *  the currently-open tab list. Propagated to each resulting checkpoint. */
  isArchived?: boolean;
}

/**
 * Build the list of revertable checkpoints across one or more chat tabs,
 * resolved against the project's current staged-op state. Each checkpoint
 * carries its owning tab id/name so the UI can display and dispatch per-tab.
 *
 * Checkpoints are returned newest-first (most recent user message on top).
 * Messages with no captured baseline or with empty diffs (no ops + no DDL)
 * are filtered out — they'd show "Nothing to revert" and clutter the list.
 */
export function buildCheckpointList(
  tabs: CheckpointTabInput[],
  currentEntities: StagedEntity[],
  resolveComponentLabel?: ComponentLabelResolver
): CheckpointDescriptor[] {
  const checkpoints: CheckpointDescriptor[] = [];
  for (const tab of tabs) {
    for (let i = 0; i < tab.messages.length; i++) {
      const msg = tab.messages[i];
      if (msg.role !== "user") continue;
      if (!msg.stagedOpIdsBeforeSend) continue;

      // Aggregate DDL ops from this message and every subsequent message
      // on the SAME tab — matches hover-button/dialog behavior.
      const ddlOps: DdlOp[] = [];
      for (let j = i; j < tab.messages.length; j++) {
        const m = tab.messages[j];
        if (m.ddlOps && m.ddlOps.length > 0) ddlOps.push(...m.ddlOps);
      }

      const messagesAffected = tab.messages.length - i;
      const summary = computeRewindSummary(
        msg.stagedOpIdsBeforeSend,
        currentEntities,
        ddlOps,
        messagesAffected,
        resolveComponentLabel
      );

      if (summary.isEmpty) continue;

      checkpoints.push({
        userMsgId: msg.id,
        tabId: tab.tabId,
        tabName: tab.tabName,
        timestamp: msg.timestamp,
        preview: truncate(msg.content, 80),
        messagesAffected,
        summary,
        isArchived: tab.isArchived === true
      });
    }
  }
  // Unified cross-tab timeline, newest first — a click on any checkpoint
  // rewinds the project back to that point in absolute time.
  checkpoints.sort((a, b) => b.timestamp - a.timestamp);
  return checkpoints;
}
